import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PROPERTIES_DATA } from '../data/properties';

const PropertiesPage = () => {
  const { t } = useTranslation();
  
  const properties = PROPERTIES_DATA.map(p => ({
    ...p,
    name: t(p.nameKey),
    location: t(p.locationKey),
    description: t(p.descKey),
  }));
  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [selectedBeds, setSelectedBeds] = useState('All');
  const [isFiltering, setIsFiltering] = useState(false);

  const bedOptions = ['All', '1-2', '3-4', '5+'];

  const getBadgeLabel = (badge) => {
    const badges = {
      popular: t('common.popular'),
      new: t('common.new'),
      luxury: t('common.luxury'),
      seaView: t('common.seaView'),
      beachfront: t('common.beachfront'),
      center: t('common.center'),
    };
    return badges[badge] || badge;
  };

  const filteredProperties = useMemo(() => {
    let result = [...properties];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.location.toLowerCase().includes(term) ||
        p.area?.toLowerCase().includes(term)
      );
    }

    if (selectedBeds !== 'All') {
      if (selectedBeds === '1-2') {
        result = result.filter(p => p.beds >= 1 && p.beds <= 2);
      } else if (selectedBeds === '3-4') {
        result = result.filter(p => p.beds >= 3 && p.beds <= 4);
      } else if (selectedBeds === '5+') {
        result = result.filter(p => p.beds >= 5);
      }
    }

    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.priceNumber - b.priceNumber);
        break;
      case 'price-high':
        result.sort((a, b) => b.priceNumber - a.priceNumber);
        break;
      case 'beds':
        result.sort((a, b) => b.beds - a.beds);
        break;
      default:
        break;
    }

    return result;
  }, [properties, searchTerm, selectedBeds, sortBy]);

  // Handle filtering loading state
  useEffect(() => {
    setIsFiltering(true);
    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 150); // Skraćeno sa 200ms na 150ms

    return () => clearTimeout(timer);
  }, [searchTerm, selectedBeds, sortBy]);

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('default');
    setSelectedBeds('All');
  };

  return (
    <div className="bg-background-dark text-white min-h-screen pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">{t('properties.pageTitle')}</h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t('properties.pageSubtitle')}
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div 
          className="bg-surface-dark border border-white/10 rounded-xl p-4 md:p-6 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-end">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">{t('common.search')}</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-white/50">
                  <span className="material-symbols-outlined text-xl">search</span>
                </span>
                <input 
                  className="block w-full rounded-lg border border-white/10 bg-white/5 py-3 pl-12 pr-4 text-white placeholder-white/30 focus:border-primary focus:ring-1 focus:ring-primary text-sm transition-all"
                  placeholder={t('properties.searchPlaceholder')}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="md:w-64">
              <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">{t('properties.bedrooms')}</label>
              <div className="grid grid-cols-4 h-[46px] bg-white/5 rounded-lg p-1 border border-white/10">
                {bedOptions.map(bed => (
                  <motion.button 
                    key={bed}
                    onClick={() => setSelectedBeds(bed)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center justify-center rounded transition-all text-xs font-medium ${
                      selectedBeds === bed 
                        ? 'bg-primary text-black font-bold shadow-lg' 
                        : 'hover:bg-white/10 text-white/50'
                    }`}
                  >
                    {bed === 'All' ? t('common.all') : bed}
                  </motion.button>
                ))}
              </div>
            </div>

            <div className="md:w-48">
              <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">{t('properties.sortBy')}</label>
              <select 
                className="block w-full rounded-lg border border-white/10 bg-white/5 py-3 px-4 text-white focus:border-primary focus:ring-1 focus:ring-primary text-sm cursor-pointer transition-all"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default" className="bg-surface-dark">{t('properties.sortDefault')}</option>
                <option value="price-low" className="bg-surface-dark">{t('properties.sortPriceLow')}</option>
                <option value="price-high" className="bg-surface-dark">{t('properties.sortPriceHigh')}</option>
                <option value="beds" className="bg-surface-dark">{t('properties.sortBeds')}</option>
              </select>
            </div>
          </div>

          <motion.div 
            className="flex items-center justify-between mt-4 pt-4 border-t border-white/5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-2">
              <motion.span 
                className="size-2 rounded-full bg-primary"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <AnimatePresence mode="wait">
                {isFiltering ? (
                  <motion.span 
                    key="filtering"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-white/70 text-sm flex items-center gap-2"
                  >
                    <motion.span 
                      className="material-symbols-outlined text-sm"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      refresh
                    </motion.span>
                    {t('properties.filtering')}
                  </motion.span>
                ) : (
                  <motion.span 
                    key="count"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-white/70 text-sm"
                  >
                    {filteredProperties.length} {t('properties.available')}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <AnimatePresence>
              {(searchTerm || selectedBeds !== 'All' || sortBy !== 'default') && (
                <motion.button 
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  onClick={clearFilters}
                  className="text-xs font-bold text-primary hover:text-primary-hover flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                  {t('common.clearFilters')}
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* Properties Grid */}
        <AnimatePresence mode="wait">
          {isFiltering ? (
            <motion.div 
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {[...Array(6)].map((_, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03, duration: 0.3 }}
                  className="bg-surface-dark rounded-xl overflow-hidden border border-white/10"
                >
                  <div className="h-64 bg-white/5 animate-pulse" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-white/10 rounded w-3/4 animate-pulse" />
                    <div className="h-3 bg-white/5 rounded w-1/2 animate-pulse" />
                    <div className="flex gap-4">
                      <div className="h-3 bg-white/5 rounded w-12 animate-pulse" />
                      <div className="h-3 bg-white/5 rounded w-12 animate-pulse" />
                      <div className="h-3 bg-white/5 rounded w-12 animate-pulse" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : filteredProperties.length === 0 ? (
            <motion.div 
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-20"
            >
              <motion.span 
                className="material-symbols-outlined text-6xl text-white/20 mb-4 block"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                search_off
              </motion.span>
              <h3 className="text-xl font-bold text-white mb-2">{t('properties.noPropertiesTitle')}</h3>
              <p className="text-white/60 mb-6">{t('properties.noPropertiesText')}</p>
              <motion.button 
                onClick={clearFilters}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary text-black px-6 py-3 rounded-lg font-bold hover:bg-primary-hover transition-colors"
              >
                {t('common.clearFilters')}
              </motion.button>
            </motion.div>
          ) : (
            <motion.div 
              key="grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {filteredProperties.map((property, index) => (
                <motion.article 
                  key={`${property.id}-${selectedBeds}-${sortBy}`} // Jedinstveni key za svaki filter
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: index * 0.03, // Skraćeno sa 0.05 na 0.03
                    ease: "easeOut"
                  }}
                  whileHover={{ y: -4 }} // Smanjeno sa -8 na -4
                  className="bg-surface-dark rounded-xl overflow-hidden border border-white/10 hover:border-primary/50 transition-all duration-300 group hover:shadow-[0_20px_40px_rgba(212,175,53,0.15)]"
                >
                  <Link to={`/properties/${property.id}`}>
                    <div className="relative h-64 overflow-hidden">
                      <motion.div 
                        className="w-full h-full bg-cover bg-center"
                        style={{ backgroundImage: `url("${property.image}")` }}
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      
                      <motion.div 
                        className={`absolute top-4 left-4 ${property.badgeColor} text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.05 }}
                      >
                        {getBadgeLabel(property.badge)}
                      </motion.div>
                      
                      {/* Price overlay */}
                      <div className="absolute bottom-4 left-4">
                        <span className="text-2xl font-bold text-primary">{property.pricePerNight}</span>
                        <span className="text-white/70 text-sm"> {t('common.perNight')}</span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors mb-2">
                        {property.name}
                      </h3>
                      
                      <p className="text-white/60 text-sm flex items-center gap-1 mb-4">
                        <span className="material-symbols-outlined text-[16px] text-primary">location_on</span>
                        {property.area}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-white/60 mb-4">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[18px]">bed</span>
                          <span>{property.beds}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[18px]">bathtub</span>
                          <span>{property.baths}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[18px]">group</span>
                          <span>{property.guests}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-end pt-4 border-t border-white/10">
                        <span className="text-primary text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1">
                          {t('common.viewDetails')}
                          <span className="material-symbols-outlined text-sm">arrow_forward</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PropertiesPage;
